import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import { 
    FolderOpen, 
    FileText, 
    Image, 
    Users, 
    Eye,
    ThumbsUp,
    ThumbsDown,
    Clock,
    Smartphone,
    Monitor,
    Tablet,
    BarChart3,
    Download,
    Map
} from 'lucide-react';
import { cn } from '../../lib/utils';
import { useDocsI18n } from '../../i18n';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export function AdminOverview({ 
    stats, 
    totalViews, 
    projects, 
    docs, 
    topDocs, 
    feedbackStats,
    detailedAnalytics = []
}) {
    const { t } = useDocsI18n();
    // Format seconds to readable time
    const formatTime = (seconds) => {
        if (seconds < 60) return `${seconds}s`;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}m ${secs}s`;
    };

    const handleExportJson = async () => {
        try {
            const token = localStorage.getItem('token');
            const headers = {};
            if (token && token.split('.').length === 3) headers.Authorization = `Bearer ${token}`;
            const res = await fetch(`${API_URL}/api/export/docs`, { headers });
            if (!res.ok) {
                toast.error(`Export fehlgeschlagen (${res.status})`);
                return;
            }
            const blob = await res.blob();
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            const ts = new Date().toISOString().replace(/[:.]/g, '-').slice(0, 19);
            a.download = `docs-export-${ts}.json`;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
            toast.success('JSON-Export heruntergeladen');
        } catch (err) {
            toast.error('Export fehlgeschlagen');
        }
    };

    const handleOpenSitemap = () => {
        window.open(`${API_URL}/api/sitemap.xml`, '_blank', 'noopener,noreferrer');
    };

    return (
        <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
        >
            <div className="flex items-center justify-between mb-8 flex-wrap gap-4">
                <h1 className="text-3xl font-mono font-bold">Dashboard</h1>
                <div className="flex items-center gap-2">
                    <Button
                        variant="outline"
                        size="sm"
                        onClick={handleOpenSitemap}
                        data-testid="open-sitemap-btn"
                    >
                        <Map className="w-4 h-4 mr-2" />
                        Sitemap (XML)
                    </Button>
                    <Button
                        variant="default"
                        size="sm"
                        onClick={handleExportJson}
                        data-testid="export-json-btn"
                    >
                        <Download className="w-4 h-4 mr-2" />
                        JSON-Export
                    </Button>
                </div>
            </div>
            
            {/* Stats Cards */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4 mb-8">
                {[
                    { id: 'statProjects', value: stats.projects, icon: FolderOpen, color: 'text-blue-500' },
                    { id: 'statDocs', value: stats.documentations, icon: FileText, color: 'text-green-500' },
                    { id: 'statMedia', value: stats.media, icon: Image, color: 'text-purple-500' },
                    { id: 'statUsers', value: stats.users, icon: Users, color: 'text-orange-500' },
                    { id: 'statViews', value: totalViews, icon: Eye, color: 'text-cyan-500' }
                ].map(stat => (
                    <Card key={stat.id} className="border-border/50 bg-card/50">
                        <CardContent className="pt-6">
                            <div className="flex items-center justify-between">
                                <div>
                                    <p className="text-sm text-muted-foreground">{t(stat.id)}</p>
                                    <p className="text-3xl font-mono font-bold">{stat.value}</p>
                                </div>
                                <stat.icon className={cn("w-8 h-8", stat.color)} />
                            </div>
                        </CardContent>
                    </Card>
                ))}
            </div>

            {/* Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Top Docs */}
                <Card className="border-border/50">
                    <CardHeader>
                        <CardTitle className="font-mono flex items-center gap-2">
                            <Eye className="w-5 h-5 text-primary" />
                            Beliebteste Dokumentationen
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {topDocs.length > 0 ? topDocs.map((doc, idx) => (
                            <div key={doc.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                <div className="flex items-center gap-3">
                                    <span className="text-lg font-bold text-muted-foreground w-6">#{idx + 1}</span>
                                    <div>
                                        <span className="font-mono text-sm">{doc.title}</span>
                                        <p className="text-xs text-muted-foreground">{doc.project_name}</p>
                                    </div>
                                </div>
                                <Badge variant="secondary" className="font-mono">
                                    {doc.views} Aufrufe
                                </Badge>
                            </div>
                        )) : (
                            <p className="text-sm text-muted-foreground">{t('noViews')}</p>
                        )}
                    </CardContent>
                </Card>

                {/* Detailed Analytics Card */}
                <Card className="border-border/50">
                    <CardHeader>
                        <CardTitle className="font-mono flex items-center gap-2">
                            <BarChart3 className="w-5 h-5 text-primary" />
                            Lesezeit & Engagement
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {detailedAnalytics.length > 0 ? detailedAnalytics.slice(0, 5).map((item, idx) => (
                            <div key={item.doc_id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                <div className="min-w-0 flex-1">
                                    <span className="font-mono text-sm block truncate">{item.title}</span>
                                    <span className="text-xs text-muted-foreground">{item.total_sessions} Sessions</span>
                                </div>
                                <div className="flex items-center gap-3 ml-2 text-xs">
                                    <span className="flex items-center gap-1 text-blue-500" title="Durchschn. Lesezeit">
                                        <Clock className="w-3 h-3" /> {formatTime(item.avg_read_time)}
                                    </span>
                                    <span className="flex items-center gap-1 text-green-500" title="Scroll-Tiefe">
                                        {item.avg_scroll_depth}%
                                    </span>
                                </div>
                            </div>
                        )) : (
                            <p className="text-sm text-muted-foreground text-center py-4">{t('noEngagement')}</p>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Projects */}
                <Card className="border-border/50">
                    <CardHeader>
                        <CardTitle className="font-mono">Letzte Projekte</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {projects.slice(0, 5).map(p => (
                            <div key={p.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                <div className="flex items-center gap-2">
                                    <div className="w-3 h-3 rounded" style={{ backgroundColor: p.color }} />
                                    <span className="font-mono text-sm">{p.name}</span>
                                </div>
                                <Badge variant="outline" className="text-xs">{p.slug}</Badge>
                            </div>
                        ))}
                        {projects.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">{t('noProjectsCard')}</p>
                        )}
                    </CardContent>
                </Card>

                {/* Recent Docs */}
                <Card className="border-border/50">
                    <CardHeader>
                        <CardTitle className="font-mono">Letzte Dokumentationen</CardTitle>
                    </CardHeader>
                    <CardContent>
                        {docs.slice(0, 5).map(d => (
                            <div key={d.id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                <span className="font-mono text-sm truncate flex-1">{d.title}</span>
                                <Badge variant={d.is_published ? 'default' : 'secondary'} className="text-xs ml-2">
                                    {d.is_published ? t('published') : t('draft')}
                                </Badge>
                            </div>
                        ))}
                        {docs.length === 0 && (
                            <p className="text-sm text-muted-foreground text-center py-4">{t('noDocsCard')}</p>
                        )}
                    </CardContent>
                </Card>

                {/* Feedback Stats Card */}
                <Card className="border-border/50">
                    <CardHeader>
                        <CardTitle className="font-mono flex items-center gap-2">
                            <ThumbsUp className="w-5 h-5 text-green-500" />
                            {t('feedbackTitle')}
                        </CardTitle>
                    </CardHeader>
                    <CardContent>
                        {feedbackStats.length > 0 ? feedbackStats.slice(0, 5).map((item) => (
                            <div key={item.doc_id} className="flex items-center justify-between py-2 border-b border-border/50 last:border-0">
                                <div className="min-w-0 flex-1">
                                    <span className="font-mono text-sm block truncate">{item.title}</span>
                                    <span className="text-xs text-muted-foreground">{item.project_name}</span>
                                </div>
                                <div className="flex items-center gap-2 ml-2">
                                    <span className="text-xs text-green-500 flex items-center gap-1">
                                        <ThumbsUp className="w-3 h-3" /> {item.helpful}
                                    </span>
                                    <span className="text-xs text-red-500 flex items-center gap-1">
                                        <ThumbsDown className="w-3 h-3" /> {item.not_helpful}
                                    </span>
                                </div>
                            </div>
                        )) : (
                            <p className="text-sm text-muted-foreground text-center py-4">{t('noFeedback')}</p>
                        )}
                    </CardContent>
                </Card>
            </div>
        </motion.div>
    );
}
